import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { trackPromise } from 'react-promise-tracker';
import { fetchWrapper } from '_utils/fetch-wrapper';
import { blobBrowserDownload } from '_utils/files/blobBrowserDownload';

// create slice

const name = 'mapcenter';
const initialState = createInitialState();
const extraActions = createExtraActions();
const reducers = createReducers();
const extraReducers = createExtraReducers();
const slice = createSlice({ name, initialState, reducers, extraReducers });

// exports

export const mapCenterAction = { ...slice.actions, ...extraActions };
export const mapCenterReducer = slice.reducer;

// implementation

function createInitialState() {
    return {
        userData: null
    }
}

function createExtraActions() {

    const baseUrl = `${process.env.REACT_APP_API_URL}/api/Account`;

    return {
        get: get(),
        insert: insert(),
        update: update(),
        getExpiredUserDetails: getExpiredUserDetails(),
        getPartiallyApprovedDetails: getPartiallyApprovedDetails(),
        getPartiallyVerifiedDetails: getPartiallyVerifiedDetails(),
        getRegistrationDetails: getRegistrationDetails(),
        updateExpired: updateExpired(),
        updatePartiallyApproved: updatePartiallyApproved(),
        updatePartiallyVerified: updatePartiallyVerified(),  
        updateAdmin: updateAdmin(),
        downloadFile: downloadFile()      
    };

    function downloadFile() {
        return createAsyncThunk(
            `${name}/downloadFile`,
            async (fileName, { rejectWithValue }) => {
                try {
                  const { blob, headers } = await fetchWrapper.getFile(
                    `${baseUrl}/map-center/download-file?filename=${encodeURIComponent(fileName)}`
                  );
                  blobBrowserDownload(blob, headers, fileName);
                  return true;
                } catch (error) {
                  return rejectWithValue(error);
                }
            }
        );
    }

    function get() {
        return createAsyncThunk(
            `${name}/getUserData`,
            async ({ id }, { rejectWithValue }) => {
                try {
                    const url = new URL(`${baseUrl}/GetRegisterMapCentreAsync/${id}`);
                    const response = await trackPromise(fetchWrapper.get(url.toString()));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function getExpiredUserDetails() {
        return createAsyncThunk(
            `${name}/getExpiredUserDetails`,
            async (_, { rejectWithValue }) => {
                try {
                    const url = new URL(`${baseUrl}/map-center/reactivate-details`);
                    const response = await trackPromise(fetchWrapper.get(url.toString()));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function getPartiallyApprovedDetails() {
        return createAsyncThunk(
            `${name}/getPartiallyApprovedDetails`,
            async (_, { rejectWithValue }) => {
                try {
                    const url = new URL(`${baseUrl}/map-center/partially-approved-registration`);
                    const response = await trackPromise(fetchWrapper.get(url.toString()));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function getPartiallyVerifiedDetails() {
        return createAsyncThunk(
            `${name}/getPartiallyVerifiedDetails`,
            async (_, { rejectWithValue }) => {
                try {
                    const url = new URL(`${baseUrl}/map-center/partially-verified-registration`);
                    const response = await trackPromise(fetchWrapper.get(url.toString()));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function getRegistrationDetails() {
        return createAsyncThunk(
            `${name}/getRegistrationDetails`,
            async (verificationId, { rejectWithValue }) => {
                try {
                    const url = new URL(`${baseUrl}/map-center/registration-details?id=${verificationId}`);
                    const response = await trackPromise(fetchWrapper.get(url.toString()));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function insert() {
        return createAsyncThunk(
            `${name}/insert`,
            async ({ id, transformedData }, { rejectWithValue }) => {
                try {
                    const response = await trackPromise(fetchWrapper.post(`${baseUrl}/Register-MC`, { Data: transformedData }));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function update() {
      return createAsyncThunk(
        `${name}/update`,
        async (payload, { rejectWithValue }) => {
          try {
            const response = await trackPromise(
              fetchWrapper.put(`${baseUrl}/putMapCenter-MC`, {
                Data: payload,
              }),
            );
            return response;
          } catch (error) {
            return rejectWithValue(error);
          }
        },
      );
    }

    function updateAdmin() {
      return createAsyncThunk(
        `${name}/updateAdmin`,
        async (payload, { rejectWithValue }) => {
          try {
            const response = await trackPromise(
              fetchWrapper.put(`${baseUrl}/map-center/update-details`, {
                Data: payload,
              }),
            );
            return response;
          } catch (error) {
            return rejectWithValue(error);
          }
        },
      );
    }

    function updateExpired() {
      return createAsyncThunk(
        `${name}/update`,
        async (payload, { rejectWithValue }) => {
          try {
            const response = await trackPromise(
              fetchWrapper.put(`${baseUrl}/map-center/reactivate-user`, {
                Data: payload,
              }),
            );
            return response;
          } catch (error) {
            return rejectWithValue(error);
          }
        },
      );
    }

    function updatePartiallyApproved() {
      return createAsyncThunk(
        `${name}/update`,
        async (payload, { rejectWithValue }) => {
          try {
            const response = await trackPromise(
              fetchWrapper.put(
                `${baseUrl}/map-center/partially-approved-registration`,
                { Data: payload },
              ),
            );
            return response;
          } catch (error) {
            return rejectWithValue(error);
          }
        },
      );
    }

    function updatePartiallyVerified() {
      return createAsyncThunk(
        `${name}/update`,
        async (payload, { rejectWithValue }) => {
          try {
            const response = await trackPromise(
              fetchWrapper.put(
                `${baseUrl}/map-center/partially-verified-registration`,
                { Data: payload },
              ),
            );
            return response;
          } catch (error) {
            return rejectWithValue(error);
          }
        },
      );
    }
}

function createReducers() {
    return {
        clear
    };

    function clear(state) {
        state.userData = null;
    }
}

function createExtraReducers() {
    return (builder) => {
        get();
        getExpiredUserDetails();
        getRegistrationDetails();
        getPartiallyApprovedDetails();
        getPartiallyVerifiedDetails();

        function get() {
            var { pending, fulfilled, rejected } = extraActions.get;
            builder
                .addCase(pending, (state) => {
                    state.userData = { loading: true };
                })
                .addCase(fulfilled, (state, action) => {
                    const data = action.payload;
                    state.userData = data.Data;
                })
                .addCase(rejected, (state, action) => {
                    state.userData = { error: action.error };
                });
        }

        function getExpiredUserDetails() {
            var { pending, fulfilled, rejected } = extraActions.getExpiredUserDetails;
            builder
                .addCase(pending, (state) => {
                    state.userData = { loading: true };
                })
                .addCase(fulfilled, (state, action) => {
                    const data = action.payload;
                    state.userData = data;   
                })
                .addCase(rejected, (state, action) => {
                    state.userData = { error: action.error };
                });
        }

        function getPartiallyApprovedDetails() {
            var { pending, fulfilled, rejected } = extraActions.getPartiallyApprovedDetails;
            builder
                .addCase(pending, (state) => {
                    state.userData = { loading: true };
                })
                .addCase(fulfilled, (state, action) => {
                    const data = action.payload;
                    state.userData = data;   
                })
                .addCase(rejected, (state, action) => {
                    state.userData = { error: action.error };
                });
        }

        function getPartiallyVerifiedDetails() {
            var { pending, fulfilled, rejected } = extraActions.getPartiallyVerifiedDetails;
            builder
                .addCase(pending, (state) => {
                    state.userData = { loading: true };
                })
                .addCase(fulfilled, (state, action) => {
                    const data = action.payload;
                    state.userData = data;   
                })
                .addCase(rejected, (state, action) => {
                    state.userData = { error: action.error };
                });
        }

        function getRegistrationDetails() {
            var { pending, fulfilled, rejected } = extraActions.getRegistrationDetails;
            builder
                .addCase(pending, (state) => {
                    state.userData = { loading: true };
                })
                .addCase(fulfilled, (state, action) => {
                    const data = action.payload;
                    state.userData = data;   
                })
                .addCase(rejected, (state, action) => {
                    state.userData = { error: action.error };
                });
        }
    };
}